import Box from '@material-ui/core/Box';
import Modal from '@material-ui/core/Modal';
import Tabs from '@material-ui/core/Tabs';
import { ReactComponent as ArrowDownIcon } from 'assets/icon/arrowDown.svg';
import { ReactComponent as CloseIcon } from 'assets/icon/close.svg';
import { ReactComponent as CopyIcon } from 'assets/icon/copy.svg';
import { find } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { datetimeFormat } from 'utils/dateFormat';
import {
  HeadTab,
  HistorieHeader,
  ItemTitle,
  List,
  ListItem,
  Title,
  TooltipButton,
} from './index.view';

const CloseButton = styled.div`
  background: #f3f6f9;
  border-radius: 8px;
  width: 30px;
  height: 30px;
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  z-index: 1;
`;

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export type ItemProps = {
  current: boolean;
  customTextList?: string;
  datetime: Date;
  label?: string;
  creatorName: string;
  value: string;
};

type VersionHistoryProps = {
  id: string;
  items: Array<ItemProps>;
  handleOnSelect?: (item: any) => void;
  setSelectLabel?: React.Dispatch<React.SetStateAction<ItemProps | undefined>>;
  value: string | undefined;
  defaultTitle?: string;
  withoutVersionSwitch?: boolean;
  withoutActualVersion?: boolean;
  withoutChangesTab?: boolean;
  customMessage?: string;
};

const VersionHistory: React.VFC<VersionHistoryProps> = ({
  id,
  items,
  handleOnSelect,
  setSelectLabel,
  value: itemSelected,
  defaultTitle,
  withoutVersionSwitch = false,
  withoutActualVersion = false,
  withoutChangesTab = false,
  customMessage = undefined,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState(-1);
  const [value, setValue] = React.useState(0);

  const selectLabel = useMemo(() => {
    return find(items, ['value', itemSelected]);
  }, [items, itemSelected]);

  const handleOpen = () => {
    if (!withoutVersionSwitch) {
      setOpen(true);
    }
  };

  const handleClose = () => setOpen(false);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const dynamicSort = useCallback(
    (property: string) => {
      return function (a: any, b: any) {
        const result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
        return result * sortOrder;
      };
    },
    [sortOrder],
  );

  useEffect(() => {
    if (setSelectLabel) {
      setSelectLabel(selectLabel);
    }
  }, [selectLabel]);

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Title>{defaultTitle}</Title>

        <TooltipButton
          id={`${id}-tooltip-btn`}
          type="button"
          onClick={handleOpen}
          show={open}
          noBorder={withoutVersionSwitch}
        >
          <div className="wrapp">
            <CopyIcon />
          </div>
          {customMessage && <span>{customMessage}</span>}
          {selectLabel && !customMessage && (
            <span>
              {`${selectLabel.creatorName} vom ${datetimeFormat(selectLabel.datetime)}
               ${selectLabel.current && !withoutActualVersion ? `(${t('actualVersion')})` : ''}`}
            </span>
          )}
          {!selectLabel && !customMessage && <span>{t('unsavedVersion')}</span>}
        </TooltipButton>
      </Box>
      {!withoutVersionSwitch && (
        <Modal
          open={open}
          id="version-history-modal"
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              borderRadius: '8px',
              transform: 'translate(-50%, -50%)',
              maxWidth: 690,
              width: '90%',
              bgcolor: 'background.paper',
              boxShadow: 24,
              height: '100%',
              maxHeight: '50vh',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              overflow: 'auto',
              '@media(max-width: 690px)': {
                maxHeight: '80vh',
              },
            }}
          >
            <Box>
              <CloseButton onClick={handleClose}>
                <CloseIcon />
              </CloseButton>

              <Box
                sx={{
                  pb: 1,
                }}
              >
                <Tabs
                  value={value}
                  onChange={handleChange}
                  aria-label="basic tabs example"
                  TabIndicatorProps={{
                    style: {
                      display: 'none',
                    },
                  }}
                >
                  <HeadTab
                    label="Historie"
                    {...a11yProps(0)}
                    style={{
                      padding: 0,
                      textTransform: 'none',
                    }}
                  />
                  {!withoutChangesTab && (
                    <HeadTab
                      label="Änderungen"
                      {...a11yProps(1)}
                      style={{
                        padding: 0,
                        textTransform: 'none',
                      }}
                    />
                  )}
                </Tabs>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                overflow: 'scroll',
                flexDirection: 'column',
                flexGrow: 1,
              }}
            >
              <TabPanel value={value} index={0}>
                <HistorieHeader>
                  <div
                    onClick={() => {
                      setSortOrder((sort) => (sort === 1 ? -1 : 1));
                    }}
                  >
                    Versionsdatum
                    <ArrowDownIcon
                      className={`arrow ${sortOrder === 1 ? '' : 'arrow-up'}`}
                      id="arrow"
                    />
                  </div>
                </HistorieHeader>
                <List>
                  {items.sort(dynamicSort('datetime')).map((item: ItemProps) => (
                    <ListItem
                      onClick={() => {
                        handleOnSelect && handleOnSelect(item);
                        handleOnSelect && handleClose();
                      }}
                      key={item?.value}
                      selected={item?.value === itemSelected}
                      isPointer={!!handleOnSelect}
                    >
                      <ItemTitle>{datetimeFormat(item.datetime)}</ItemTitle>
                      <div>{item.creatorName}</div>
                    </ListItem>
                  ))}
                </List>
              </TabPanel>
              {!withoutChangesTab && <TabPanel value={value} index={1}></TabPanel>}
            </Box>
          </Box>
        </Modal>
      )}
    </div>
  );
};

export default VersionHistory;
